import {
  OrganizationDTO,
  PayerDTO,
  TenantDTO,
  useGetOrganizationByID,
  useGetTenant,
  useTenantUpdatePayer,
  updatePayerByIDBody,
  getListPayersQueryKey,
  getGetPayerByIDQueryKey,
  getListPayersForTenantQueryKey,
} from '@/api/trafikportalen';
import { InputText } from '../Inputs/InputText';
import { InputPostalCode } from '../Inputs/InputPostalCode';
import { FormSection } from '../FormSection';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormElement } from '../FormElement';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { useCallback, useMemo, useState } from 'react';
import { FormSubmitActions } from '../FormSubmitActions';
import { toast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import Button from '../core/Button';
import { IconEdit } from '@/icons';
import { cn } from '@/helpers/classnames';
import { formatDateTime } from '@/helpers/formatting';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import {
  type PossibleDetails,
  type PickRequiredFields,
  payerDetailsTranslations,
  possiblePayerDetails,
  validatePayerDetails,
} from '@/helpers/payerDetails';
import { trimData } from '@/helpers/trimData';

interface Props {
  payer: PayerDTO;
}

export const EditablePayerItem = ({ payer }: Props) => {
  const { data: tenant } = useGetTenant();
  const { data: organization } = useGetOrganizationByID(payer.organization_id);

  if (!tenant || !organization) {
    return null;
  }

  return (
    <PayerForm payer={payer} tenant={tenant} organization={organization} />
  );
};

const PayerForm = ({
  payer,
  tenant,
  organization,
}: {
  payer: PayerDTO;
  tenant: TenantDTO;
  organization: OrganizationDTO;
}) => {
  const [ref] = useAutoAnimateConditional<HTMLFormElement>();
  const updatePayer = useTenantUpdatePayer();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();

  const [editMode, setEditMode] = useState(false);

  const requiredProps = organization.is_internal
    ? tenant.required_internal_invoicing_details
    : tenant.required_external_invoicing_details;

  const optionalProps = organization.is_internal
    ? tenant.optional_internal_invoicing_details
    : tenant.optional_external_invoicing_details;

  const allProps = useMemo(
    () => [...requiredProps, ...optionalProps],
    [requiredProps, optionalProps],
  );

  type RequiredDetails = (typeof requiredProps)[number];
  type RequiredDetailsForm = PayerDTO & {
    details: PickRequiredFields<PossibleDetails, RequiredDetails>;
  };

  const missingProps = useMemo(() => {
    return requiredProps.filter((prop) => !payer.details[prop]);
  }, [requiredProps, payer.details]);

  const schema = z.intersection(
    updatePayerByIDBody,
    z
      .object({
        reference: z.string().min(1),
      })
      .and(
        z.custom<RequiredDetailsForm>().superRefine((data, ctx) => {
          validatePayerDetails({
            requiredProps,
            optionalProps,
            details: data.details,
            formatMessage,
            fieldPath: 'details',
            ctx,
          });
        }),
      ),
  );

  const methods = useForm<RequiredDetailsForm>({
    defaultValues: payer,
    resolver: zodResolver(schema, {
      errorMap: useCustomErrorMap(),
    }),
    mode: 'all',
  });

  const startEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  const cancelEdit = useCallback(() => {
    methods.reset();
    setEditMode(false);
  }, [methods]);

  const onSubmit: SubmitHandler<RequiredDetailsForm> = useCallback(
    (data) => {
      const onlyRelevantDetails = Object.fromEntries(
        Object.entries(data.details).filter(([key]) =>
          allProps.includes(key as RequiredDetails),
        ),
      );

      updatePayer.mutate(
        {
          payerID: payer.id,
          data: trimData({ ...data, details: onlyRelevantDetails }),
        },
        {
          onSuccess: async () => {
            toast.success(
              {
                title: 'Fakturamottagare uppdaterad',
              },
              { autoClose: 3000 },
            );
            await Promise.all([
              queryClient.invalidateQueries({
                queryKey: getListPayersForTenantQueryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: getListPayersQueryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: getGetPayerByIDQueryKey(payer.id),
              }),
            ]);

            setEditMode(false);
          },
          onError: (error) => {
            console.error(error);
            toast.error(
              {
                title: 'Kunde inte uppdatera fakturamottagaren',
                description: error.message,
              },
              { autoClose: 3000 },
            );
          },
        },
      );
    },
    [payer.id, queryClient, allProps, updatePayer],
  );

  return (
    <FormProvider {...methods}>
      <form
        ref={ref}
        className="flex w-full flex-col gap-4"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <FormSection
          onClickButton={startEdit}
          editMode={editMode}
          canSeeButton={checkUserPermissions({ permission: 'tenant:editor' })}
          className={cn(missingProps.length > 0 && 'border-red-500 bg-red-50')}
        >
          <div className="flex flex-wrap gap-x-4 text-text-800">
            <p>
              <FormattedMessage defaultMessage="Skapad" />{' '}
              {formatDateTime(payer.created_at)}
            </p>
            <p>
              <FormattedMessage defaultMessage="Uppdaterad" />{' '}
              {formatDateTime(payer.updated_at)}
            </p>
          </div>
          <FormElement
            label={<FormattedMessage defaultMessage="Referens" />}
            input={
              <InputText
                name="reference"
                control={methods.control}
                editMode={editMode}
                error={methods.formState.errors.reference}
              />
            }
          />
          <FormElement
            label={<FormattedMessage defaultMessage="Adress" />}
            input={
              <InputText
                name="address.street"
                control={methods.control}
                editMode={editMode}
                error={methods.formState.errors.address?.street}
              />
            }
          />
          <FormElement
            label={<FormattedMessage defaultMessage="Postort" />}
            input={
              <InputText
                name="address.city"
                control={methods.control}
                editMode={editMode}
                error={methods.formState.errors.address?.city}
              />
            }
          />
          <FormElement
            label={<FormattedMessage defaultMessage="Postnummer" />}
            input={
              <InputPostalCode
                name="address.zip_code"
                control={methods.control}
                editMode={editMode}
                error={methods.formState.errors.address?.zip_code}
              />
            }
          />
          <b>
            <FormattedMessage defaultMessage="Måste sättas av systemadministratör innan månadsskifte:" />
          </b>
          {possiblePayerDetails
            .filter((detail) => requiredProps.includes(detail))
            .map((detail) => (
              <FormElement
                key={detail}
                label={payerDetailsTranslations[detail]}
                required={editMode}
                input={
                  <InputText
                    name={`details.${detail}`}
                    control={methods.control}
                    editMode={editMode}
                    error={methods.formState.errors.details?.[detail]}
                    helperTextReserveSpace
                    type="number"
                  />
                }
              />
            ))}
          {optionalProps?.length > 0 && (
            <p className="text-text-800">
              <FormattedMessage defaultMessage="Valfria faktureringsdetaljer:" />
            </p>
          )}
          {possiblePayerDetails
            .filter((detail) => optionalProps.includes(detail))
            .map((detail) => (
              <FormElement
                key={detail}
                label={payerDetailsTranslations[detail]}
                input={
                  <InputText
                    name={`details.${detail}`}
                    control={methods.control}
                    editMode={editMode}
                    error={methods.formState.errors.details?.[detail]}
                    helperTextReserveSpace
                    type="number"
                  />
                }
              />
            ))}
          {!editMode && (
            <Button
              variant="ghost"
              startIcon={<IconEdit />}
              onClick={startEdit}
              className="ml-auto"
            >
              <FormattedMessage defaultMessage="Redigera" />
            </Button>
          )}
          <FormSubmitActions
            active={editMode}
            createMode={false}
            onCancel={cancelEdit}
          />
        </FormSection>
      </form>
    </FormProvider>
  );
};
